import { Injectable } from '@angular/core';
import { ChartData } from './chart.interface';
import { of, Subject, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '@config/configuration.service';
// import { MarketSocketService, QUOTE_UPDATE_TYPE, QuoteSocketUpdateI } from '@socket/index';
import { QuoteStoreService } from '../quote-store';
import { filter } from 'rxjs/operators';
import { MarketSocketService, QUOTE_UPDATE_TYPE, QuoteSocketUpdateI, SingleIndexQuotes, SOCKET_MSG_TYPES, subscribeQuoteParams } from '../websocket';
import { CandleIntervals, MarketIDMap } from '@app/_shared/shared.enums';
import { CandleUpdateObj, ChartDataResponse, ChartingFilters } from '@app/_shared/modules/chart/chart.interface';
import { Utils } from '@app/_helpers';
import { OhlcDataRequest } from '../../../../utProto/chartingData_pb';
import { Request } from '../../../../utProto/marketData_pb';
import { MarketWatch, SymbolRow } from '@app/app.models';
import { ChartIntervals, ChartUpdateType } from '@app/_shared/modules/chart/chart.structs';
import { MarketWatchQuote } from '@app/modules/trading/modules/market-watch/structs/market-watch.interface';

@Injectable({
    providedIn: 'root'
})
export class ChartingService {
    lastSubscribedInstrument = {
        market: 0,
        securityId: 0
    };
    private _selectedSymbol: MarketWatch = null;
    private _selectedInterval: ChartIntervals = null;
    private _intervalSubscription: Subscription = null;
    requestIdMap = new Map<number, boolean>();
    private _lastTime = null;
    private _latestCandle: ChartDataResponse = {
        open_price: 0,
        close_price: 0,
        high_price: 0,
        low_price: 0,
        timestamp: this._lastTime || new Date().getTime(),
        volume: 0
    };
    public symbolIdMap = new Map<string, SymbolRow>();
    private _lastRecodedCandle: ChartDataResponse = null;
    private _candleSubject = new Subject<CandleUpdateObj>();
    private _chartingOptions: string[] = [];
    private _lastVolume = 0;
    private _lastQuoteVolume = 0;


    constructor(
        private http: HttpClient,
        private confService: ConfigurationService,
        private marketSocketService: MarketSocketService,
        private quoteStore: QuoteStoreService,
        private marketStoreSocket: MarketSocketService
    ) { }

    subscribeQuoteUpdates(marketID: number, securityNumber: number) {
        this.unsubscribeQuoteUpdates();
        this.lastSubscribedInstrument.market = marketID;
        this.lastSubscribedInstrument.securityId = securityNumber;
        this.marketSocketService.subscribeSymbol({market_id:marketID, security_id:securityNumber});
    }

    getOHLCData(filters) {
        const url = this.confService.applicationUrls.ohlcFirstFetch;
        return this.http.request('GET', url, {
            params: filters
        });

    }

    unsubscribeQuoteUpdates() {
        if (this.lastSubscribedInstrument?.market &&
            this.lastSubscribedInstrument?.securityId
        ) {
            this.marketSocketService.unSubscribeSymbol(
                {market_id:this.lastSubscribedInstrument.market,
                security_id:this.lastSubscribedInstrument.securityId}
            )
        }
    }

    getFeed() {
        return this.marketSocketService.getFeedSubject<QuoteSocketUpdateI>(QUOTE_UPDATE_TYPE.QUOTE)
            .pipe(
                filter((quote) => {
                    return quote.ExchangeId === MarketIDMap[this.lastSubscribedInstrument?.market] &&
                        quote.SecurityId === this.lastSubscribedInstrument?.securityId
                })
            )
    }

    generateRandomData(min: number, max: number, lastNumberOfDay = 20): ChartData[] {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - lastNumberOfDay);
        const retArr = [];
        for (let i = 0; i < lastNumberOfDay; i++) {
            const obj: ChartData = {
                time: startDate.getTime(),
                data: +((Math.random() * (max - min) + (min)).toFixed(2))
            };

            startDate.setDate(startDate.getDate() + 1);
            retArr.push(obj);
        }
        return retArr;
    }

    generateRandomBarData(min: number, max: number, lastNumberOfDay = 20): ChartData[] {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - lastNumberOfDay);
        const retArr = [];
        for (let i = 0; i < lastNumberOfDay; i++) {
            const obj: ChartData = {
                time: startDate.getTime(),
                data: [+((Math.random() * (max - min) + (min)).toFixed(2)), +((Math.random() * (max - min) + (min)).toFixed(2))]
            };

            startDate.setDate(startDate.getDate() + 1);
            retArr.push(obj);
        }
        return retArr;
    }

    fetchPLChartData() {
        return of(this.generateRandomData(-5, 25));
    }

    fetchDrawDownChartData() {
        return of(this.generateRandomData(-20, 20, 10));
    }

    fetchWilLossChartData() {
        return of(this.generateRandomBarData(10, 20, 10));
    }



    getChartingData(chartingFilter: ChartingFilters) {
        this.quoteStore.ohlcSymbolMap.set(Utils.getSymbolId(MarketIDMap[chartingFilter.market_id], chartingFilter.security_id),
            {
                trade_value: chartingFilter.trade_value, market_id: chartingFilter.market_id,
                market_lot: chartingFilter.market_lot, scrip_precision: chartingFilter.scrip_precision
            });
        const command = Utils.htonl(Request.REQUEST_OHLC_DATA);
        const req = new OhlcDataRequest();
        req.setRequestId(chartingFilter.responseId);
        this.requestIdMap.set(chartingFilter.responseId, true);
        req.setInterval(CandleIntervals[chartingFilter.candle_size]);
        req.setSymbolId(chartingFilter.symbol_id);
        req.setInstrumentName(chartingFilter.instrument_name);
        req.setStartTimestamp(+chartingFilter.from_date);
        req.setEndTimestamp(+chartingFilter.to_date);
        req.setIsMarketMovementRequest(chartingFilter.is_market_movement_response);
        const ohlcReqBuf = req.serializeBinary();
        const commandCategoryBuff = new Uint8Array(command);
        const ohlcReq = new Uint8Array(commandCategoryBuff.byteLength + commandCategoryBuff.byteLength + ohlcReqBuf.length);
        ohlcReq.set(new Uint8Array(Utils.htonl(commandCategoryBuff.byteLength +
            commandCategoryBuff.byteLength + ohlcReqBuf.length)), 0);
        ohlcReq.set(commandCategoryBuff, commandCategoryBuff.byteLength);
        ohlcReq.set(ohlcReqBuf, 8);
        this.marketSocketService.send(ohlcReq);

    }

    subscribeCandles(selectedSymbol: MarketWatch, chartInterval: ChartIntervals) {
        if (this._lastRecodedCandle) {
            this._latestCandle = this._lastRecodedCandle;
            this._lastVolume = this._lastRecodedCandle.volume;
        } else {
            this._lastTime = new Date().setSeconds(0);
            this._latestCandle = {
                open_price: 0,
                close_price: 0,
                high_price: 0,
                low_price: 0,
                timestamp: this._lastTime || new Date().setSeconds(0),
                volume: 0
            };
        }
        if (!this._selectedInterval || (chartInterval !== this._selectedInterval)) {
            this._selectedInterval = chartInterval;
            if (this._intervalSubscription) {
                this._intervalSubscription.unsubscribe();
            }
            const intervalSeconds = this.intervalToSeconds(chartInterval);
            // commenting out the interval subscription as it is not required for trading view
            // this is done for echarts. 
            //if (intervalSeconds) {
            // this._candleInterval = interval(intervalSeconds * 1000);

            // this._intervalSubscription = this._candleInterval.subscribe(val => {
            //     console.log("--------------------------------");

            //     // Converting IST epoch to GMT as charts expects quotes data in GMT.
            //     this._lastTime = this._getNextTime();
            //     console.log("lastTime", this._lastTime);

            //     this._lastVolume = this._lastQuoteVolume;
            //     this._latestCandle = {
            //         open_price: this._latestCandle.open_price,
            //         close_price: this._latestCandle.close_price,
            //         high_price: this._latestCandle.high_price,
            //         low_price: this._latestCandle.low_price,
            //         timestamp: this._lastTime || new Date(),
            //         volume: 0
            //     };
            //     //this._candleSubject.next({ data: this._latestCandle, type: ChartUpdateType.new });
            // });

            //}
        }

        if (!this._selectedSymbol || (selectedSymbol.market_id !== this._selectedSymbol.market_id || selectedSymbol.security_id !== this._selectedSymbol.security_id)) {
            if (this._selectedSymbol) {
                this._latestCandle = {
                    open_price: 0,
                    close_price: 0,
                    high_price: 0,
                    low_price: 0,
                    timestamp: this._lastTime || new Date(),
                    volume: 0
                };
                this.marketSocketService.unSubscribeFeed([this._selectedSymbol.symbol_id.toString()], SOCKET_MSG_TYPES.QUOTE);
            }
            let dataToSend = this.marketSocketService.subscribedSymbolMap.get(selectedSymbol.symbol_id.toString());
            if (dataToSend) {
                this.marketSocketService.subscribeSymbol(dataToSend, SOCKET_MSG_TYPES.QUOTE);
            } else {
                //using exchange field from selectedSymbol as it is consistent in all cases. market_id sometimes contains
                //number and sometimes string.
                const symbolData: subscribeQuoteParams = {
                    market_id: +MarketIDMap[selectedSymbol['exchange']],
                    security_id: selectedSymbol.security_id,
                    symbol_id: selectedSymbol.symbol_id,
                    scrip_precision: selectedSymbol.scrip_precision ? selectedSymbol.scrip_precision : 2
                };
                this.marketSocketService.subscribeSymbol(symbolData, SOCKET_MSG_TYPES.QUOTE);
            }
            this._subscribeSocket();
            this._selectedSymbol = selectedSymbol;
        }

        return this._candleSubject;
    }

    private _subscribeSocket() {


        this.quoteStore.getFeedSubject(QUOTE_UPDATE_TYPE.QUOTE)
            .subscribe((quote) => {
                if (quote && this._lastTime && this._selectedSymbol && (quote.ExchangeId === this._selectedSymbol['exchange'] && quote.SecurityId === this._selectedSymbol.security_id)) {
                    this.handleQuote(quote);

                }
            })


        this.quoteStore.getFeedSubject(QUOTE_UPDATE_TYPE.INDEX)
            .subscribe((quote: SingleIndexQuotes) => {
                if (quote && this._lastTime && this._selectedSymbol && (quote.ExchangeId === this._selectedSymbol['exchange'] && quote.SecurityId === this._selectedSymbol.security_id)) {
                    this.handleQuote(Utils.indexToSymbolQuote(quote))
                }
            })
    }

    handleQuote(quote: QuoteSocketUpdateI | SingleIndexQuotes) {
        quote = (quote as QuoteSocketUpdateI);


        /**
         * As observed in trading view, the chart picks the open price of the first ,
         * i.e the candle received from the first quote in the current candle interval.
         * eg if the candle interval is 1 minute, the chart picks the open price of quote
         * received at the start of the current minute.(eg: 9:15:00). Whatever that data
         * passed to the chart after this is not used except for the volume and close price. 
         * Also chart updates high and low values similarly.
         */


        this._latestCandle.open_price = this._selectedInterval.endsWith('d') ? quote.OpenPrice : quote.LastTradePrice;
        this._latestCandle.close_price = quote.LastTradePrice;
        this._latestCandle.high_price = this._selectedInterval.endsWith('d') ? quote.HighPrice : quote.LastTradePrice;
        this._latestCandle.low_price = this._selectedInterval.endsWith('d') ? quote.LowPrice : quote.LastTradePrice;

        this._latestCandle.volume = (quote.Volume ? quote.Volume : 0) - this._lastVolume;
        this._latestCandle.timestamp = quote.LastUpdateTime ? quote.LastUpdateTime : quote.LastTradeTime;
        this._candleSubject.next({ data: this._latestCandle, type: ChartUpdateType.update });
        this._lastQuoteVolume = quote.Volume;
    }

    private intervalToSeconds(chartInterval: ChartIntervals) {
        const time = +(chartInterval.slice(0, chartInterval.length - 1));
        if (chartInterval.endsWith('m')) {
            return time * 60;
        } else if (chartInterval.endsWith('h')) {
            return time * 3600;
        } else if (chartInterval.endsWith('d')) {
            return time * 3600 * 24;
        }
        else if (chartInterval.endsWith('w')) {
            return time * 3600 * 24 * 7;
        }
        else if (chartInterval.endsWith('M')) {
            return time * 3600 * 24 * 30;
        }
        else {
            return 0;
        }
    }

    changeSubscribedInterval(chartInterval: ChartIntervals) {
        this.subscribeCandles(this._selectedSymbol, chartInterval);
    }

    clearCache() {
        this._selectedSymbol = undefined;
        this._selectedInterval = undefined;
        this._lastTime = undefined;
        this._lastRecodedCandle = undefined;
        this._lastVolume = undefined;
    }
}
